import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button,
  Chip, CircularProgress, Stack, IconButton, Tooltip
} from "@mui/material";
import { Cancel, Info } from "@mui/icons-material";
import { collection, query, where, getDocs, doc, writeBatch, Timestamp, orderBy, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import Swal from "sweetalert2";

function Approvals() {
  const [pendingList, setPendingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchPendingApprovals(); }, []);

  const fetchPendingApprovals = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "fee_approvals"),
        where("status", "==", "Pending"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setPendingList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Fetch Error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Redistributes total paid amount across the installment array based on paidAmount
  const recalculateInstallments = (installments, totalPaidSoFar) => {
    let remaining = totalPaidSoFar;
    return installments.map(inst => {
      const amountDue = inst.amount - (inst.applicableDiscount || 0);
      const amountPaid = Math.min(remaining, amountDue);
      remaining -= amountPaid;
      return {
        ...inst,
        paidAmount: amountPaid,
        status: amountPaid >= amountDue ? "Paid" : amountPaid > 0 ? "Partially Paid" : "Pending"
      };
    });
  };

  const handleProcess = async (approval) => {
    const result = await Swal.fire({
      title: 'Approve Request?',
      text: `Authorize ${approval.type.replace('_', ' ')} for Receipt ${approval.receiptNo}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2e7d32'
    });

    if (!result.isConfirmed) return;

    setProcessing(true);

    try {
      // FIND FEE STRUCTURE
      const structQuery = query(
        collection(db, "fee_structures"),
        where("admissionId", "==", approval.admissionId)
      );

      const structSnap = await getDocs(structQuery);

      if (structSnap.empty) {
        throw new Error("Fee structure not found");
      }

      const structDoc = structSnap.docs[0];
      const structData = structDoc.data();

      // SAFE HISTORY
      let updatedHistory = Array.isArray(structData.paymentHistory)
        ? [...structData.paymentHistory]
        : [];

      // =========================
      // DELETE PAYMENT
      // =========================
      if (approval.type === "DELETE_PAYMENT") {

        updatedHistory = updatedHistory.filter(
          p => p.receiptNo !== approval.receiptNo
        );

      }

      // =========================
      // EDIT PAYMENT
      // =========================
      else if (approval.type === "EDIT_PAYMENT") {

        updatedHistory = updatedHistory.map(p => {

          if (p.receiptNo === approval.receiptNo) {

            return {
              ...p,
              paidAmount: Number(approval.newAmount || 0),
              updatedAt: new Date().toISOString()
            };

          }

          return p;
        });

      }

      // =========================
      // TOTAL PAID
      // =========================
      const newTotalPaid = updatedHistory.reduce(
        (sum, p) => sum + (Number(p.paidAmount) || 0),
        0
      );

      // =========================
      // TOTAL FEES
      // =========================
      const totalFees = Array.isArray(structData.heads)
        ? structData.heads.reduce(
          (sum, h) => sum + (Number(h.amount) || 0),
          0
        )
        : 0;

      // =========================
      // DISCOUNTS
      // =========================
      const totalDiscount = Array.isArray(structData.discounts)
        ? structData.discounts.reduce(
          (sum, d) => sum + (Number(d.amount) || 0),
          0
        )
        : 0;

      // =========================
      // BALANCE
      // =========================
      const balanceAmount =
        totalFees - totalDiscount - newTotalPaid;

      // =========================
      // UPDATE FIREBASE
      // =========================
      await updateDoc(doc(db, "fee_structures", structDoc.id), {
        paymentHistory: updatedHistory,
        totalPaid: newTotalPaid,
        balanceAmount: balanceAmount,
        updatedAt: Timestamp.now()
      });

      // =========================
      // MARK APPROVED
      // =========================
      await updateDoc(doc(db, "fee_approvals", approval.id), {
        status: "Approved",
        processedAt: Timestamp.now()
      });

      Swal.fire(
        "Approved",
        "Payment approval processed successfully",
        "success"
      );

      fetchPendingApprovals();

    } catch (e) {

      console.error("Approval Error:", e);

      Swal.fire(
        "Error",
        e.message,
        "error"
      );

    } finally {

      setProcessing(false);

    }
  };

  const handleReject = async (id) => {
    const { value: reason } = await Swal.fire({
      title: 'Reject Request?',
      input: 'text',
      inputPlaceholder: 'Reason...',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f'
    });

    if (reason) {
      await updateDoc(doc(db, "fee_approvals", id), {
        status: "Rejected",
        rejectionReason: reason,
        processedAt: Timestamp.now()
      });
      fetchPendingApprovals();
    }
  };

  if (loading) return <Box p={5} textAlign="center"><CircularProgress size={30} /></Box>;

  return (
    <Box p={2} bgcolor="#f8fafc" minHeight="100vh">
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Typography variant="h6" fontWeight={900}>Pending Approvals</Typography>
        <Chip label={pendingList.length} size="small" color="primary" sx={{ fontWeight: 700 }} />
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f1f5f9" }}>
            <TableRow>
              <TableCell sx={{ fontSize: '0.65rem', fontWeight: 800 }}>SR.</TableCell>
              <TableCell sx={{ fontSize: '0.65rem', fontWeight: 800 }}>STUDENT / RECEIPT</TableCell>
              <TableCell sx={{ fontSize: '0.65rem', fontWeight: 800 }}>DETAILS</TableCell>
              <TableCell sx={{ fontSize: '0.65rem', fontWeight: 800 }}>AMOUNT</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.65rem', fontWeight: 800 }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingList.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}>No pending requests</TableCell></TableRow>
            ) : (
              pendingList.map((item, index) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{item.studentName}</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={item.type === 'DELETE_PAYMENT' ? 'DELETE' : 'EDIT'}
                        size="small"
                        color={item.type === 'DELETE_PAYMENT' ? 'error' : 'primary'}
                        sx={{ height: 16, fontSize: '0.55rem', fontWeight: 900 }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{item.date} • {item.mode}</Typography>
                    {item.note && (
                      <Box sx={{ mt: 0.5, px: 1, py: 0.2, bgcolor: '#eff6ff', borderRadius: 1, borderLeft: '3px solid #3b82f6' }}>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#1e40af' }}>
                          <Info sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />
                          {item.note}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.type === "EDIT_PAYMENT" ? (
                      <Stack>
                        <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>₹{item.oldAmount}</Typography>
                        <Typography variant="body2" fontWeight={900} color="#3b82f6">₹{item.newAmount}</Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" fontWeight={900} color="#ef4444">₹{item.amount}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={processing}
                        onClick={() => handleProcess(item)}
                        sx={{ fontSize: '0.65rem', fontWeight: 700, px: 2 }}
                      >
                        Approve
                      </Button>
                      <IconButton size="small" color="error" onClick={() => handleReject(item.id)} disabled={processing}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Approvals;